import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Loader2, Send, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAY_STYLES = {
  paid: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
  pending: "bg-amber-400/10 text-amber-300 border-amber-400/25",
  failed: "bg-red-400/15 text-red-300 border-red-400/30",
  expired: "bg-slate-400/15 text-slate-400 border-slate-400/30",
  refunded: "bg-violet-400/15 text-violet-300 border-violet-400/30",
};

export const PaymentSection = ({ inquiryId, prospectEmail, onPaymentsChange, onNoteAdded }) => {
  const [payments, setPayments] = useState([]);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("Strategy Consultation Fee");
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const onChangeRef = useRef(onPaymentsChange);
  useEffect(() => { onChangeRef.current = onPaymentsChange; }, [onPaymentsChange]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/inquiries/${inquiryId}/payments`, { withCredentials: true });
      setPayments(res.data);
      setLoadError(false);
      onChangeRef.current?.(res.data);
    } catch { setLoadError(true); }
  }, [inquiryId]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    await Promise.all(payments.filter((p) => p.payment_status === "pending")
      .map((p) => axios.get(`${API}/payments/status/${p.session_id}`).catch(() => null)));
    await load();
    setRefreshing(false);
  };

  const createLink = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount."); return; }
    if (!description.trim()) { toast.error("Enter a description."); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/inquiries/${inquiryId}/payment-link`,
        { amount: amt, description: description.trim(), origin_url: window.location.origin },
        { withCredentials: true });
      onNoteAdded?.(res.data.note);
      toast.success(`Payment link for $${amt.toFixed(2)} emailed to ${prospectEmail}.`);
      setAmount("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create payment link.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="payment-section" className="mt-8 border-t border-white/10 pt-6">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-medium flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-emerald-400" /> Payments
          <span className="text-sm text-slate-500 font-normal">({payments.length})</span>
        </h3>
        {payments.length > 0 && (
          <button data-testid="refresh-payments-btn" onClick={refresh} disabled={refreshing}
            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh status
          </button>
        )}
      </div>
      {loadError && (
        <p data-testid="payments-load-error" className="mt-3 text-sm text-red-300">Couldn't load payments. <button onClick={load} className="underline hover:text-white">Try again</button></p>
      )}
      {payments.length > 0 && (
        <div className="mt-4 space-y-2">
          {payments.map((p) => (
            <div key={p.session_id} data-testid={`payment-row-${p.session_id}`} className="flex items-center justify-between gap-3 bg-[#05071F] border border-white/10 p-3">
              <div className="min-w-0">
                <p className="text-sm text-slate-200 truncate">{p.description}</p>
                <p className="text-xs text-slate-500">
                  {new Date(p.created_at).toLocaleString()}
                  {p.reminder_of && <span data-testid={`reminder-tag-${p.session_id}`} className="ml-2 text-violet-300/80">· reminder sent</span>}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm text-white font-medium">${Number(p.amount).toFixed(2)}</span>
                <span className={`text-xs font-semibold uppercase tracking-widest border rounded-full px-2.5 py-0.5 ${PAY_STYLES[p.payment_status] || PAY_STYLES.pending}`}>
                  {p.payment_status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <div className="relative sm:w-36">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
          <input data-testid="payment-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)}
            placeholder="250.00" inputMode="decimal"
            className="w-full bg-[#05071F] border border-white/15 rounded-lg pl-8 pr-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-emerald-400" />
        </div>
        <input data-testid="payment-description-input" value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. Strategy Consultation Fee, Service Deposit, Invoice #104"
          className="flex-1 bg-[#05071F] border border-white/15 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-emerald-400" />
        <button data-testid="send-payment-link-btn" onClick={createLink} disabled={busy}
          className="inline-flex items-center justify-center gap-2 text-sm bg-emerald-400 text-[#05071F] font-semibold px-5 py-2.5 rounded-full hover:bg-emerald-300 transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Email Payment Link
        </button>
      </div>
      <p className="mt-2 text-xs text-slate-500">Creates a secure Stripe Checkout link, emails it to the prospect, and logs it as a note. Unpaid links get one friendly reminder (with a fresh link) after 3 days.</p>
    </div>
  );
};
